/**
 * POC license banner (Layer 3 of the expiry defense).
 *
 * Sticky top bar shown only when POC_MODE. Polls GET /poc/status every 5 min:
 *   - >7 days left: subtle info bar with days remaining + token budget used
 *   - <=7 days: yellow warning
 *   - expired: red full-width bar; on hard expiry render a blocking overlay
 * Includes a "Request Extension" button (calls requestExtension()).
 *
 * STUB — Pkg F implements polling + render states.
 */
import React, { useEffect, useState } from 'react';
import { POC_MODE, getPocStatus, requestExtension, PocStatus } from '../lib/pocApi';
import { Icon } from './ui';
import { useTranslation } from 'react-i18next';
import { parseServerDate } from '../i18n/format';

export default function PocLicenseBanner(): JSX.Element | null {
  const { t } = useTranslation();
  const [status, setStatus] = useState<PocStatus | null>(null);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [requestingExtension, setRequestingExtension] = useState(false);
  const [requestMessage, setRequestMessage] = useState<string | null>(null);

  // Poll status on mount and every 5 minutes
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const data = await getPocStatus();
        if (data && typeof data === 'object' && 'days_remaining' in data) {
          setStatus(data as PocStatus);
        }
      } catch (error) {
        console.warn('Failed to fetch POC status:', error);
      }
    };

    fetchStatus();
    const interval = setInterval(fetchStatus, 5 * 60 * 1000); // 5 minutes
    return () => clearInterval(interval);
  }, []);

  const handleRequestExtension = async () => {
    setRequestingExtension(true);
    try {
      await requestExtension();
      setRequestMessage('Extension request sent. Your CEO will review it shortly.');
      setShowRequestDialog(false);
      // Refetch status after a brief delay
      setTimeout(async () => {
        const data = await getPocStatus();
        if (data && typeof data === 'object' && 'days_remaining' in data) {
          setStatus(data as PocStatus);
        }
      }, 1000);
    } catch (error) {
      setRequestMessage(t('components.pocLicenseBanner.errorWithMessage', { message: error instanceof Error ? error.message : t('components.pocLicenseBanner.unknownError') }));
    } finally {
      setRequestingExtension(false);
    }
  };

  if (!POC_MODE || !status) return null;

  const daysRemaining = status.days_remaining;
  const isExpired = status.expired;
  const isWarning = !isExpired && daysRemaining <= 7;

  let bannerClass = 'bg-blue-50 border-blue-200 text-blue-900 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-100';
  let iconColor = 'text-blue-600 dark:text-blue-400';

  if (isExpired) {
    bannerClass = 'bg-red-50 border-red-200 text-red-900 dark:bg-red-900/20 dark:border-red-800 dark:text-red-100';
    iconColor = 'text-red-600 dark:text-red-400';
  } else if (isWarning) {
    bannerClass = 'bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-100';
    iconColor = 'text-amber-600 dark:text-amber-400';
  }

  return (
    <>
      {/* Sticky banner */}
      <div className={`sticky top-0 z-30 border-b px-6 py-3 flex items-center justify-between ${bannerClass}`}>
        <div className="flex items-center gap-3">
          <Icon
            name={isExpired ? 'warning' : isWarning ? 'clock' : 'info'}
            size={18}
            className={iconColor}
          />
          <div>
            <span className="font-semibold">
              {isExpired
                ? t('components.pocLicenseBanner.pocHasExpired')
                : isWarning
                ? `Warning: ${daysRemaining} day${daysRemaining === 1 ? '' : 's'} remaining`
                : `${daysRemaining} day${daysRemaining === 1 ? '' : 's'} remaining`}
            </span>
            {status.token_usage_today !== undefined && status.daily_token_budget !== undefined && (
              <div className="text-sm opacity-90 mt-0.5">{t('components.pocLicenseBanner.tokenBudgetUsedToday', { v0: status.token_usage_today.toLocaleString(), v1: status.daily_token_budget.toLocaleString() })}</div>
            )}
          </div>
        </div>
        <button
          onClick={() => setShowRequestDialog(true)}
          className="px-3 py-1.5 rounded text-sm font-medium bg-white dark:bg-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-700 transition"
        >
          {t('components.pocLicenseBanner.requestExtension')}
        </button>
      </div>

      {/* Expired overlay (full-screen blocking) */}
      {isExpired && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-zinc-900 rounded-lg shadow-2xl max-w-md w-full p-8">
            <div className="text-center">
              <Icon name="warning" size={48} className="text-red-600 dark:text-red-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-red-900 dark:text-red-100 mb-2">
                {t('components.pocLicenseBanner.pocExpired')}
              </h2>
              <p className="text-zinc-600 dark:text-zinc-400 mb-6">{t('components.pocLicenseBanner.yourPOCTrialEndedOnRequest', { v0: parseServerDate(status.expires_at).toLocaleDateString() })}</p>
              <button
                onClick={() => setShowRequestDialog(true)}
                className="w-full px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded font-medium transition"
              >
                <Icon name="mail" size={16} className="inline mr-2" />
                {t('components.pocLicenseBanner.requestExtension')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request extension dialog */}
      {showRequestDialog && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-zinc-900 rounded-lg shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-bold mb-4">{t('components.pocLicenseBanner.requestExtension')}</h3>
            <p className="text-zinc-600 dark:text-zinc-400 mb-6">
              {t('components.pocLicenseBanner.sendAnExtensionRequestToYour')}
            </p>
            {requestMessage && (
              <div className={`text-sm p-3 rounded mb-4 ${
                requestMessage.startsWith('Error')
                  ? 'bg-red-100 text-red-900 dark:bg-red-900/30 dark:text-red-100'
                  : 'bg-green-100 text-green-900 dark:bg-green-900/30 dark:text-green-100'
              }`}>
                {requestMessage}
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => setShowRequestDialog(false)}
                className="flex-1 px-4 py-2 rounded border border-zinc-300 dark:border-zinc-600 text-zinc-900 dark:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
              >
                {t('components.pocLicenseBanner.cancel')}
              </button>
              <button
                onClick={handleRequestExtension}
                disabled={requestingExtension}
                className="flex-1 px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium transition"
              >
                {requestingExtension ? t('components.pocLicenseBanner.sending') : t('components.pocLicenseBanner.sendRequest')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
